import { Component } from '@verndale/core';
import Video from './video';
import { isMobile } from '../helpers';
import Swiper, { Pagination, Navigation } from 'swiper';
import { KEYS } from '../constants';

Swiper.use([Pagination, Navigation]);

class Module extends Component {
  setupDefaults() {
    this.dom = {
      $container: this.el._$('.swiper-container'),
      $wrapper: this.el._$('.swiper-wrapper'),
      $pagination: this.el._$('.swiper-pagination'),
      $focusables: this.el._$$('.video__container'),
      $next: this.el._$('.content-carousel__button-next'),
      $prev: this.el._$('.content-carousel__button-prev')
    };

    this.isVideoList = this.el.classList.contains('video-list');
    this.dotLabel = this.el.dataset.dotLabel;
    this.nextLabel = this.el.dataset.nextLabel;
    this.previousLabel = this.el.dataset.previousLabel;

    if (this.isVideoList) {
      this.dom.$slideContainer = this.el._$('.video-list__slide-container');
      this.dom.$videoListThumbs = this.el._$('.video-list__slide-thumbs');
      this.dom.$thumbnails = this.dom.$videoListThumbs._$$('.video__container');
      this.dom.$slideActiveTitle = this.el._$('.video-row__heading');
      this.dom.$slideActiveDesc = this.el._$('.video-row__text');

      this.initSliderList();
    } else {
      this.initSlider();
    }

    this.makeBulletsAccessible();
  }

  makeBulletsAccessible() {
    this.dom.$bullets = this.el._$$('.swiper-pagination-bullet');

    this.dom.$bullets?.forEach(($bullet, idx) => {
      if (this.dotLabel) $bullet.setAttribute('aria-label', this.dotLabel.concat(idx));
      $bullet.setAttribute('role', 'button');
      $bullet.setAttribute('tabindex', 0);
      $bullet._on('keydown', this.handleBulletKeyDown.bind(this));
    });

    if (this.nextLabel) this.dom.$next.setAttribute('aria-label', this.nextLabel);
    if (this.prevLabel) this.dom.$prev.setAttribute('aria-label', this.prevLabel);

    this.dom.$next._on('keydown', this.handleBulletKeyDown.bind(this));
    this.dom.$prev._on('keydown', this.handleBulletKeyDown.bind(this));
  }

  handleBulletKeyDown(e) {
    if (e.keyCode !== KEYS.ENTER && e.keyCode !== KEYS.SPACE) return;
    e.target.click();
  }

  handleSwiperKeyUp(e, swiper) {
    e.preventDefault();
    const isTab = e.keyCode === KEYS.TAB;
    if (!isTab) return;

    const isTabBack = isTab && e.shiftKey;
    const focusEl = document.activeElement.closest('.swiper-slide');
    const slideIndex = Array.prototype.indexOf.call(focusEl.parentNode.children, focusEl);

    // reset default browser content displacement after user tabbing
    this.dom.$wrapper.parentElement.scrollLeft = 0;

    if (slideIndex !== swiper.activeIndex) isTabBack ? swiper.slidePrev(0) : swiper.slideNext(0);
  }

  // If clicked on any slide replace the active one
  handleListElemClick(swiper, slideIndex = null) {
    const idx = slideIndex >= 1 ? slideIndex - 1 : swiper.clickedIndex;
    const selectedSlide = swiper.slides[idx]?._$('.video-row__container--list');
    const previousActiveSlide = this.el._$$('.video-list__slide-active')[0];
    const selectedId = selectedSlide?._$('.video__container').dataset.videoId;
    const isCreated = this.currentVideo && (this.currentVideo.videoId === selectedId);
    const activeSlide = isCreated ? null : selectedSlide?.cloneNode(true);

    if (isCreated && !document.getElementById(selectedId)) {
      this.currentVideo.initVideo();
    } else if (activeSlide) {
      activeSlide.className = 'video-row__container video-list__slide-active';

      this.currentVideo = new Video(activeSlide);
      this.currentVideo.videoId = selectedSlide?._$('.video__container').dataset.videoId;

      this.dom.$slideContainer.removeChild(previousActiveSlide);
      this.dom.$slideContainer.prepend(activeSlide);

      this.dom.$thumbnails.forEach($thumb => {
        $thumb.classList.remove('video-carousel-thumb--active');
        $thumb.blur();
      });

      this.dom.$thumbnails[idx].classList.add('video-carousel-thumb--active');
      this.dom.$focusables[idx].focus();
    }
  }

  handleListElementKeyUp(e, swiper) {
    e.preventDefault();

    if (e.keyCode === KEYS.ENTER || e.keyCode === KEYS.SPACE || e.keyCode === KEYS.TAB) {
      const focusEl = document.activeElement.closest('.swiper-slide');
      const slideIndex = Array.prototype.indexOf.call(focusEl.parentNode.children, focusEl);

      if (e.keyCode === KEYS.TAB) {
        const page = Math.floor(slideIndex / swiper.params.slidesPerColumn);

        // reset default browser content displacement after user tabbing
        this.dom.$wrapper.parentElement.scrollLeft = 0;

        if (swiper.activeIndex !== page) swiper.slideTo(page);
      } else {
        this.handleListElemClick(swiper, slideIndex + 1);
      }
    }
  }

  stopVideos() {
    this.el._$$('iframe').forEach(element => {
      element.contentWindow.postMessage('{"event":"command", "func":"pauseVideo", "args":""}', '*');
    });
  }

  initSlider() {
    const that = this;

    this.slider = new Swiper(this.dom.$container, {
      centeredSlides: true,
      autoHeight: true,
      spaceBetween: 10,
      slidesPerView: 1,
      noSwiping: true,
      noSwipingClass: 'swiper-no-swiping',
      watchOverflow: true,
      keyboard: {
        enabled: true
      },
      pagination: {
        el: this.dom.$pagination,
        clickable: true
      },
      navigation: {
        nextEl: this.dom.$next,
        prevEl: this.dom.$prev
      },
      on: {
        init(swiper) {
          if (swiper.slides.length < 2) {
            that.dom.$pagination.style.display = 'none';
            that.dom.$next.style.display = 'none';
            that.dom.$prev.style.display = 'none';
          }

          swiper.el._on('keyup', e => { that.handleSwiperKeyUp(e, swiper) });
        },
        slideChange(swiper) {
          that.stopVideos();
        }
      }
    });
  }

  initSliderList() {
    const that = this;

    this.slider = new Swiper(this.dom.$container, {
      centeredSlides: false,
      loopFillGroupWithBlank: true,
      breakpoints: {
        360: {
          spaceBetween: 0,
          slidesPerGroup: 4,
          slidesPerView: 4
        },
        768: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          slidesPerColumn: 4
        },
        1024: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          slidesPerColumn: 4
        }
      },
      navigation: {
        nextEl: this.dom.$next,
        prevEl: this.dom.$prev
      },
      pagination: {
        el: this.dom.$pagination,
        clickable: true,
        type: 'custom',
        renderCustom(swiper, current) {
          const total = swiper.slides.length;
          let group, startGroup, endGroup;

          if (!isMobile()) {
            group = swiper.params.slidesPerColumn,
            startGroup = swiper.activeIndex * group + 1,
            endGroup = current * group > total ? total : current * group;
          } else {
            group = swiper.params.slidesPerGroup;
            startGroup = swiper.activeIndex + 1;
            endGroup = swiper.activeIndex + group;
          }

          if (total <= group) {
            that.dom.$pagination.style.display = 'none';
            that.dom.$next.style.display = 'none';
            that.dom.$prev.style.display = 'none';
            return;
          }

          return `${startGroup} - ${endGroup} of ${total}`;
        }
      },
      a11y: {
        prevSlideMessage: this.previousLabel,
        nextSlideMessage: this.nextLabel
      },
      on: {
        init(swiper) {
          // Take the first slide of carousel and fill the active slide with the first one
          // Also verify if it is greater than 1 to display the carousel

          if (swiper.slides.length > 0) {
            that.dom.$videoListThumbs.classList.remove('video-list__slide-thumbs--hidden');
            that.dom.$thumbnails[0].classList.add('video-carousel-thumb--active');

            const firstSlideContainer = swiper.slides[0]._$('.video-row__container--list');

            const activeSlide = firstSlideContainer.cloneNode(true);
            activeSlide.className = 'video-row__container video-list__slide-active';

            const newVideo = new Video(activeSlide);
            newVideo.videoId = firstSlideContainer._$('.video__container').dataset.videoId;

            that.dom.$slideContainer.prepend(activeSlide);
          } else {
            that.dom.$videoListThumbs.classList.add('video-list__slide-thumbs--hidden');
          }

          swiper.el._on('keyup', e => { that.handleListElementKeyUp(e, swiper) });
        },
        click(swiper) {
          that.handleListElemClick(swiper);
        },
        resize(swiper) {
          swiper.pagination.update();
        },
        slideChange(swiper) {
          that.stopVideos();
        }
      }
    });

  }

}

export default Module;
